import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';
import { UserMaintenanceService } from '../../_services/user-maintenance/user-maintenance.service';
import { FeatureCatalogItem, MaintenanceRole, RoleFeatureItem } from '../../_models/admin/user-maintenance.models';
import { AccessCheckService } from '../../_services/access-check/access-check.service';
import { AccessCheckCandidate, AccessCheckResult, AccessCheckStep, AccessCheckUserSuggestion } from '../../_models/admin/access-check.models';
import { PageGate, PageGates } from '../../_constants/page-gates';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { FeatureSwitch } from '../../_models/admin/feature-switch.models';

/** The page select's value for picking a feature, switch and role directly. */
const OTHER = 'other';

/** The prefix LeapDb's Seed.FeaturePurpose.sql puts on a feature no reachable screen reads. */
const NOT_USED_PREFIX = 'Not used:';

/**
 * Role → feature grant editor. Pick a role; every feature is listed with a checkbox showing whether that role
 * grants it. A grant is one per role and feature, never per brand. Toggling a cell saves immediately (optimistic;
 * reverts on error). An admin-only feature (System Controls) can be granted only to ADMIN: for any other role its
 * checkbox is disabled unless it is already on, so it can still be revoked. Each feature shows its purpose (dbo.Feature.Purpose) beneath its key, and
 * features whose purpose starts "Not used:" are muted — granting them changes nothing. Gated on RoleFeatureAdmin.
 *
 * Behind admin.role-features-agreed-set, the role is compared with the agreed set: a summary of how many places it
 * differs (with a Show only differences filter), each differing feature marked, who last changed each feature, and a
 * feature the agreed set keeps to staff roles locked for a client role unless it is already on, so it can be revoked.
 * The counts follow each toggle at once. Off, the screen is as it was.
 *
 * Behind admin.access-check, a Check access panel asks the API whether a user (by user name or email) can use a page from
 * the side menu, or any feature / switch / role picked under Other, and shows the verdict and each gate it passed or failed.
 * When several live accounts share the email, it lists them to choose one, and checks the chosen one by its user id.
 * As the user box is typed into (2 characters or more), it suggests matching accounts (the shared app-typeahead);
 * choosing one checks that account by its user id until the box is typed into again. A failed suggestion search shows
 * no suggestions.
 */
@Component({
  selector: 'role-feature-matrix',
  templateUrl: 'role-feature-matrix.component.html',
  standalone: false
})
export class RoleFeatureMatrixComponent implements OnInit {

  public roles: MaintenanceRole[] = [];
  public selectedRoleId: number = null;
  public features: RoleFeatureItem[] = [];
  public loading: boolean = false;
  /** Hide features no reachable screen reads (purpose starts "Not used:"). On by default. */
  public hideUnused: boolean = true;
  /** admin.role-features-agreed-set is on: compare the role with the agreed set. */
  public agreedSetOn: boolean = false;
  /** Show only the features where the role differs from the agreed set. */
  public onlyDifferences: boolean = false;

  /** For the template's `*appFeatureSwitch`. */
  public readonly featureKeys = FeatureSwitchKeys;
  /** The page select's groups: the side-menu pages, then the admin pages whose route declares its gates. */
  public readonly menuPages = PageGates.filter(g => g.source === 'menu');
  public readonly routePages = PageGates.filter(g => g.source === 'route');
  public readonly otherPage = OTHER;

  /** Check access: the user's user name or email and the page (a label from PageGates, or OTHER). */
  public checkUser: string = '';
  public checkPage: string = PageGates[0].label;
  public otherFeature: string = '';
  public otherSwitchKey: string = '';
  public otherRole: string = '';
  /** Other's feature and switch lists, loaded the first time Other is chosen. */
  public featureOptions: FeatureCatalogItem[] = [];
  public switchOptions: FeatureSwitch[] = [];
  /** The switch list needs FeatureSwitchAdmin; without it Other takes a typed switch key instead. */
  public switchListFailed: boolean = false;
  private otherListsRequested: boolean = false;
  public checking: boolean = false;
  public checkResult: AccessCheckResult = null;
  /** What the last result was checked against, for the verdict line. */
  public checkedTarget: string = '';
  public checkError: string = null;
  /** The live accounts sharing the email last checked, kept while one of them is checked. */
  public checkCandidates: AccessCheckCandidate[] = [];
  /** The candidate being checked, or null. */
  public chosenUserId: number = null;
  /** The account chosen from the suggestions, checked by its id; cleared when the box is typed into again. */
  public selectedUserId: number = null;
  /** The user box's suggestions: live accounts matching the text (the API returns 404 while the switch is off). */
  public readonly searchUsers = (q: string): Observable<AccessCheckUserSuggestion[]> => this.accessCheckService.searchUsers(q);
  public readonly suggestionUserName = (s: AccessCheckUserSuggestion): string => s?.userName ?? '';

  /**
   * The features the table shows: only the differences from the agreed set while that filter is on and there are
   * some, otherwise all of them, or only the used ones while hideUnused is on.
   */
  public get visibleFeatures(): RoleFeatureItem[] {
    if (this.filteringDifferences) {
      return this.features.filter(f => this.isDifferent(f));
    }
    return this.hideUnused ? this.features.filter(f => !this.isNotUsed(f)) : this.features;
  }

  /** How many features hideUnused is currently hiding. */
  public get hiddenCount(): number {
    return this.hideUnused && !this.filteringDifferences ? this.features.length - this.visibleFeatures.length : 0;
  }

  /** The grid shows only the differences from the agreed set. */
  private get filteringDifferences(): boolean {
    return this.onlyDifferences && this.differenceCount > 0;
  }

  /** The selected role is being compared with the agreed set: the switch is on and the API sent the comparison. */
  public get comparing(): boolean {
    return this.agreedSetOn && this.features.some(f => f.agreed != null);
  }

  /** The selected role's name. */
  public get roleName(): string {
    return this.roles.find(r => r.id === this.selectedRoleId)?.description ?? 'This role';
  }

  /** How many features are on here but off in the agreed set. */
  public get onNotAgreedCount(): number {
    return this.features.filter(f => this.isOnNotAgreed(f)).length;
  }

  /** How many features are off here but on in the agreed set. */
  public get offButAgreedCount(): number {
    return this.features.filter(f => this.isOffButAgreed(f)).length;
  }

  /** How many places the role differs from the agreed set. */
  public get differenceCount(): number {
    return this.onNotAgreedCount + this.offButAgreedCount;
  }

  /** "Custodian differs from the agreed set in 2 places". */
  public get differenceHeading(): string {
    const n = this.differenceCount;
    return `${this.roleName} differs from the agreed set in ${n} ${n === 1 ? 'place' : 'places'}`;
  }

  /** How many are on here but off in the agreed set, and how many the other way, leaving out a zero. */
  public get differenceDetail(): string {
    const count = (n: number) => `${n} ${n === 1 ? 'feature is' : 'features are'}`;
    const parts: string[] = [];
    if (this.onNotAgreedCount > 0) {
      parts.push(`${count(this.onNotAgreedCount)} on here but off in the agreed set.`);
    }
    if (this.offButAgreedCount > 0) {
      parts.push(`${count(this.offButAgreedCount)} off here but on in the agreed set.`);
    }
    return parts.join(' ');
  }

  constructor(
    private service: UserMaintenanceService,
    private errorModalHelper: ErrorModalHelperService,
    private accessCheckService: AccessCheckService,
    private featureSwitchService: FeatureSwitchService
  ) { }

  ngOnInit(): void {
    // canUseGuard has already required RoleFeatureAdmin on the route.
    this.service.getRoles().subscribe({
      next: roles => this.roles = roles,
      error: error => this.errorModalHelper.show(error.message, error)
    });
    this.featureSwitchService.isEnabled(FeatureSwitchKeys.roleFeaturesAgreedSet).subscribe({
      next: on => this.agreedSetOn = !!on,
      error: () => this.agreedSetOn = false
    });
  }

  /** The user box was typed into: forget the chosen account. */
  public onUserInput(value: string): void {
    this.checkUser = value ?? '';
    this.selectedUserId = null;
  }

  /** A suggestion was chosen: the box shows its user name, and Check checks exactly that account. */
  public chooseSuggestion(suggestion: AccessCheckUserSuggestion): void {
    this.checkUser = suggestion.userName;
    this.selectedUserId = suggestion.userId;
  }

  public loadFeatures(): void {
    if (!this.selectedRoleId) {
      this.features = [];
      return;
    }
    this.loading = true;
    this.service.getRoleFeatures({ roleId: this.selectedRoleId }).subscribe({
      next: features => {
        this.loading = false;
        this.features = features;
      },
      error: error => {
        this.loading = false;
        this.errorModalHelper.show(error.message, error);
      }
    });
  }

  /** True when the feature is admin-only and the selected role is not ADMIN, and it is not already on. */
  public isLocked(feature: RoleFeatureItem): boolean {
    const role = this.roles.find(r => r.id === this.selectedRoleId);
    return !!feature.adminOnly && !feature.enabled && (role?.code ?? '').toUpperCase() !== 'ADMIN';
  }

  /** True when the agreed set keeps the feature to staff roles, the selected role is a client role, and it is not on. */
  public isStaffLocked(feature: RoleFeatureItem): boolean {
    return this.agreedSetOn && !!feature.staffOnly && !feature.enabled;
  }

  /** True when the feature is on for the role but off in the agreed set. */
  public isOnNotAgreed(feature: RoleFeatureItem): boolean {
    return this.agreedSetOn && feature.agreed === false && feature.enabled;
  }

  /** True when the feature is off for the role but on in the agreed set. */
  public isOffButAgreed(feature: RoleFeatureItem): boolean {
    return this.agreedSetOn && feature.agreed === true && !feature.enabled;
  }

  /** True when the role differs from the agreed set on this feature. */
  public isDifferent(feature: RoleFeatureItem): boolean {
    return this.isOnNotAgreed(feature) || this.isOffButAgreed(feature);
  }

  /** True when the feature's purpose marks it as read by no reachable screen. */
  public isNotUsed(feature: RoleFeatureItem): boolean {
    return !!feature.purpose && feature.purpose.startsWith(NOT_USED_PREFIX);
  }

  public toggle(feature: RoleFeatureItem, checked: boolean): void {
    if (checked && (this.isLocked(feature) || this.isStaffLocked(feature))) {
      return;
    }
    const previous = feature.enabled;
    feature.enabled = checked; // optimistic
    this.service.setRoleFeature({
      roleId: this.selectedRoleId,
      featureId: feature.featureId,
      enabled: checked
    }).subscribe({
      error: error => {
        feature.enabled = previous; // revert on failure
        this.errorModalHelper.show('Could not update the feature grant. Please try again.', error);
      }
    });
  }

  /** The page select changed: the first time Other is chosen, load its feature and switch lists. */
  public onCheckPageChange(page: string): void {
    this.checkPage = page;
    if (page !== OTHER || this.otherListsRequested) {
      return;
    }
    this.otherListsRequested = true;
    this.service.getFeatures().subscribe({
      next: features => this.featureOptions = [...(features ?? [])].sort((a, b) => a.description.localeCompare(b.description)),
      error: error => this.errorModalHelper.show(error.message, error)
    });
    this.featureSwitchService.getSwitches().subscribe({
      next: switches => this.switchOptions = [...(switches ?? [])].sort((a, b) => a.switchKey.localeCompare(b.switchKey)),
      error: () => this.switchListFailed = true
    });
  }

  /** "Display name (key)", or the key alone when the switch has no display name. */
  public switchLabel(featureSwitch: FeatureSwitch): string {
    return featureSwitch.displayName ? `${featureSwitch.displayName} (${featureSwitch.switchKey})` : featureSwitch.switchKey;
  }

  /** The gates of the selected page, or the chosen feature / switch / role while Other is selected. */
  private selectedGates(): Pick<PageGate, 'label' | 'feature' | 'switchKey' | 'role'> {
    if (this.checkPage === OTHER) {
      const feature = this.otherFeature.trim();
      return { label: feature || this.otherSwitchKey.trim() || this.otherRole.trim() || 'this',
        feature, switchKey: this.otherSwitchKey, role: this.otherRole };
    }
    return PageGates.find(g => g.label === this.checkPage) ?? PageGates[0];
  }

  /**
   * Checks the user typed in, or the account chosen from the suggestions by its id; a new check forgets any accounts
   * listed for an earlier one.
   */
  public checkAccess(): void {
    this.checkCandidates = [];
    this.chosenUserId = null;
    this.runCheck(this.selectedUserId ?? undefined);
  }

  /** One of several accounts sharing the email was chosen: check exactly that one, keeping the list. */
  public chooseCandidate(candidate: AccessCheckCandidate): void {
    this.chosenUserId = candidate.userId;
    this.runCheck(candidate.userId);
  }

  private runCheck(userId?: number): void {
    if (!this.checkUser.trim()) {
      this.checkError = 'Enter the user name or email of the user to check.';
      this.checkResult = null;
      return;
    }
    const gates = this.selectedGates();
    this.checking = true;
    this.checkError = null;
    this.checkResult = null;
    const query = { user: this.checkUser, feature: gates.feature, switchKey: gates.switchKey, role: gates.role };
    this.accessCheckService.check(userId != null ? { ...query, userId } : query).subscribe({
      next: result => {
        this.checking = false;
        this.checkResult = result;
        this.checkedTarget = gates.label;
        if (result?.candidates?.length) {
          this.checkCandidates = result.candidates;
        }
      },
      error: error => {
        this.checking = false;
        this.checkError = error?.status === 404 ? 'Check access is not switched on in this environment.'
          : error?.status === 400 ? 'Enter the user name or email of the user to check.'
          : (error?.message || 'The access check failed. Please try again.');
      }
    });
  }

  /** The first step that failed, if any. */
  public get firstFailure(): AccessCheckStep | undefined {
    return this.checkResult?.steps?.find(s => !s.passed);
  }

  /** "Can use Email history", or "Cannot use Email history: <first failing step>". */
  public get verdict(): string {
    if (!this.checkResult) {
      return '';
    }
    if (this.checkResult.allowed) {
      return `Can use ${this.checkedTarget}`;
    }
    const failure = this.firstFailure;
    const reason = failure ? (failure.detail || `${failure.gate} ${failure.name} failed`) : 'no gate passed';
    return `Cannot use ${this.checkedTarget}: ${reason}`;
  }
}
